'use client';

import { useEffect, useState } from 'react';
import { useUiMotionEnabled } from './useMotion';
import { countUpDurationMs } from './countUpDuration';


function makeCubicBezier(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sampleX = (t: number): number => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number): number => ((ay * t + by) * t + cy) * t;
  return (x: number): number => {
    let t = Math.max(0, Math.min(1, x));
    for (let i = 0; i < 8; i++) {
      const dx = (3 * ax * t + 2 * bx) * t + cx;
      if (Math.abs(dx) < 1e-6) break;
      t -= (sampleX(t) - x) / dx;
    }
    return sampleY(Math.max(0, Math.min(1, t)));
  };
}
const easeCount = makeCubicBezier(0.2, 0.8, 0.2, 1);

type TweenState = {
  target: number;
  displayed: number;
};

export function useCountUp(amount: number, enabled = true): number {
  const motionEnabled = useUiMotionEnabled();
  const animates = enabled && motionEnabled;
  const [tween, setTween] = useState<TweenState>({ target: amount, displayed: amount });

  if (tween.target !== amount) {
    setTween({ target: amount, displayed: animates ? tween.displayed : amount });
  }

  useEffect(() => {
    if (tween.displayed === tween.target) return;
    const start = tween.displayed;
    const target = tween.target;
    const delta = target - start;
    const durationMs = countUpDurationMs(delta);
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number): void => {
      const p = Math.min(1, (now - t0) / durationMs);
      const value = Math.round(start + delta * easeCount(p));
      setTween((s) => (s.target === target ? { target, displayed: value } : s));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tween.target]);

  return tween.displayed;
}
